import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { fleetKeys } from '@/hooks/fleet-keys';
import { useFleetTask } from '@/hooks/use-fleet-task';
import { apiClient, fleetPath } from '@/lib/api-client';
import type { LifecycleActionDef } from '@/lib/lifecycle-actions';
import type { ActionResponse, ServerDetail, TaskStatus } from '@/types/fleet';

const TERMINAL_STATUSES: ReadonlySet<TaskStatus> = new Set(['success', 'failure', 'stopped']);

/** A lifecycle run currently being tracked for a server. */
export interface ActiveLifecycleRun {
  taskId: number;
  action: LifecycleActionDef;
  /** Drives the status-badge pending overlay until the task settles. */
  showOverlay: boolean;
}

interface TriggerVariables {
  endpoint: string;
  serverName: string;
  reason?: string;
}

export interface UseLifecycleAction {
  /** Triggers a lifecycle action — POST → toast → track the task. */
  run: (action: LifecycleActionDef, reason: string) => void;
  /** True while the trigger POST is in flight. */
  isSubmitting: boolean;
  /** The lifecycle task currently tracked, if any. */
  activeRun: ActiveLifecycleRun | null;
  /** Dismiss the task-status panel — does not cancel the task. */
  dismiss: () => void;
}

/** What the lifecycle endpoints answer: a task (decommission) or a plain ack. */
type LifecycleResponse = Partial<ActionResponse> & { ok?: boolean };

/** POST /api/admin/fleet/servers/:name/{decommission,quarantine,recover}. */
function useTriggerLifecycle(): UseMutationResult<LifecycleResponse, Error, TriggerVariables> {
  return useMutation({
    mutationFn: ({ endpoint, serverName, reason }) =>
      apiClient.post<LifecycleResponse>(
        fleetPath(`/servers/${encodeURIComponent(serverName)}/${endpoint}`),
        reason !== undefined ? { reason } : undefined,
      ),
  });
}

/**
 * Orchestrates the destructive per-server lifecycle flow (decommission /
 * quarantine / recover). These hit the dedicated Fleet lifecycle endpoints
 * (NOT the generic provisioning runner) and are polled via `useFleetTask`.
 *
 * Decommission mirrors `useFleetAction`: POST → toast with task id → track →
 * pending overlay (never an optimistic status flip) → on success invalidate
 * the server + summary queries, on failure surface an error toast.
 * Quarantine and recover apply at once server-side, so they invalidate
 * straight away and track nothing.
 */
export function useLifecycleAction(server: ServerDetail): UseLifecycleAction {
  const queryClient = useQueryClient();
  const triggerMutation = useTriggerLifecycle();

  const [activeRun, setActiveRun] = useState<ActiveLifecycleRun | null>(null);
  const settledRef = useRef<number | null>(null);

  const trackedTask = useFleetTask(activeRun?.taskId);

  const invalidateServer = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: fleetKeys.server(server.name) });
    void queryClient.invalidateQueries({ queryKey: fleetKeys.summary() });
    // The list shows the status too; don't leave it stale until its next poll.
    void queryClient.invalidateQueries({ queryKey: fleetKeys.serverLists() });
  }, [queryClient, server.name]);

  const run = useCallback(
    (action: LifecycleActionDef, reason: string): void => {
      triggerMutation.mutate(
        {
          endpoint: action.endpoint,
          serverName: server.name,
          reason: action.reasonRequired ? reason : undefined,
        },
        {
          onSuccess: ({ task_id }) => {
            settledRef.current = null;

            // quarantine and recover are registry-only (planet-proxy-KB#589):
            // the API answers {ok: true}, the change has already happened, and
            // there is no task to poll. Keyed on the response, not only on the
            // action, so an API from before that change (which still returned
            // a task for quarantine) is tracked rather than reported as done.
            if (task_id === undefined) {
              setActiveRun(null);
              toast.success(`${action.label} applied to ${server.name}.`);
              invalidateServer();
              return;
            }

            setActiveRun({ taskId: task_id, action, showOverlay: !action.immediate });
            toast.success(`${action.label} queued (task #${task_id}).`);
          },
          onError: (err) => {
            toast.error(`${action.label} failed to start: ${err.message}`);
          },
        },
      );
    },
    [server.name, triggerMutation, invalidateServer],
  );

  // React to the tracked Fleet task settling — fire exactly once per task.
  useEffect(() => {
    const task = trackedTask.data;

    if (!task || !activeRun) return;
    if (!TERMINAL_STATUSES.has(task.status)) return;
    if (settledRef.current === task.task_id) return;

    settledRef.current = task.task_id;

    if (task.status === 'success') {
      toast.success(`${activeRun.action.label} succeeded (task #${task.task_id}).`);
      invalidateServer();
      setActiveRun((prev) => (prev ? { ...prev, showOverlay: false } : prev));
    } else {
      toast.error(
        `${activeRun.action.label} ${task.status} (task #${task.task_id}). See the task panel.`,
      );
      setActiveRun((prev) => (prev ? { ...prev, showOverlay: false } : prev));
    }
  }, [trackedTask.data, activeRun, invalidateServer]);

  const dismiss = useCallback(() => setActiveRun(null), []);

  return {
    run,
    isSubmitting: triggerMutation.isPending,
    activeRun,
    dismiss,
  };
}
